/**
 * ProPlusPromoVideo — iter87.512
 *
 * Self-hosted 30s Pro+ promo video on `/rivalry-pro`, directly below the
 * hero and above the 01–04 feature carousel. Served from our own domain
 * at /videos/rivalry-pro.mp4 (not the external upload link), with a
 * poster frame grabbed ~1s in so the section never shows a blank black
 * box before the reader taps play.
 *
 * Has a voiceover, so autoplay stays OFF by design — reader opts in
 * with a real tap. Border/glow uses the operator-specified #2F80FF
 * (distinct from the `sky-400`/`volt` tokens already on this page, per
 * explicit hex request).
 */
import React from "react";
import { Play } from "lucide-react";

export const PRO_PLUS_VIDEO_SRC = "/videos/rivalry-pro.mp4";
export const PRO_PLUS_VIDEO_POSTER = "/videos/rivalry-pro-poster.jpg";

export default function ProPlusPromoVideo() {
    const videoRef = React.useRef(null);
    const [playing, setPlaying] = React.useState(false);

    return (
        <section
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pb-14 sm:pb-20"
            aria-label="Pro+ 30-second tour"
            data-testid="pro-plus-promo-video-section"
        >
            <p
                className="text-[10px] uppercase tracking-[0.3em] text-sky-400 font-black mb-4 text-center"
                data-testid="pro-plus-promo-video-heading"
            >
                See Pro+ in 30 seconds
            </p>
            <div className="max-w-[900px] mx-auto">
                <div
                    className="relative w-full aspect-video rounded-2xl overflow-hidden border-2 border-[#2F80FF]/60 bg-black shadow-[0_0_44px_rgba(47,128,255,0.35)]"
                    data-testid="pro-plus-promo-video-player"
                >
                    <video
                        ref={videoRef}
                        className="w-full h-full object-cover"
                        controls
                        playsInline
                        preload="metadata"
                        poster={PRO_PLUS_VIDEO_POSTER}
                        onPlay={() => setPlaying(true)}
                        onPause={() => setPlaying(false)}
                        data-testid="pro-plus-promo-video-element"
                    >
                        <source src={PRO_PLUS_VIDEO_SRC} type="video/mp4" />
                        Your browser doesn't support embedded video — you can still{" "}
                        <a href={PRO_PLUS_VIDEO_SRC} className="text-sky-400 underline">
                            download the clip
                        </a>
                        .
                    </video>

                    {!playing && (
                        <button
                            type="button"
                            onClick={() => videoRef.current?.play()}
                            aria-label="Play the Pro+ tour video"
                            data-testid="pro-plus-promo-video-play-overlay"
                            className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/25 transition-colors group"
                        >
                            <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-sky-400 flex items-center justify-center border-2 border-black shadow-[0_0_28px_rgba(47,128,255,0.6)] group-hover:scale-105 transition-transform">
                                <Play className="w-7 h-7 sm:w-8 sm:h-8 text-black ml-1" fill="currentColor" />
                            </span>
                        </button>
                    )}
                </div>
            </div>
        </section>
    );
}
